import React, { useMemo, useState } from "react";
import { TextInput } from "App/common/TextInput";
import { COMMENT_BLOCKS } from "App/common/Comment/blocks";
import "./TrackCommentInput.scss";
import { PERMISSION } from "@highnote/server/src/core/shared-util";
import { getAuthId, useAuth } from "App/components/Auth";
import { useSpaceContext } from "App/common/useSpace";
import { JOIN_ENTITY_TRIGGER } from "App/components/Auth/util";
import { useSeekRef } from "../Carousel/useSeekRef";
import { useSpaceCommentsContext } from "App/common/useSpaceComments";
import { useTrack } from "App/components/useEntities/useTrack";
import { v4 as uuidv4 } from "uuid";
import { useFocusedCard } from "App/common/useFocusedCard";
import { PermissionTooltip } from "App/components/PermissionTooltip";
import {
  Comment,
  COMMENT_BLOCK_TYPE,
} from "@highnote/server/src/core/entities";
import { CommentButton } from "./CommentButton";
import { CommentInputActions } from "./CommentInputActions";
import { CommentTimestamp } from "./CommentTimestamp";
import { useAppRequiredDialog } from "../useAppRequiredDialog";

export const TrackCommentInput = () => {
  const { space, spaceId } = useSpaceContext();
  const { isAllowed } = useAuth();
  const { track, currentTrackVersion } = useTrack();
  const { focusedCardId } = useFocusedCard();
  const [text, setText] = useState("");
  const { addSpaceComment } = useSpaceCommentsContext();
  const { joinEntity } = useAuth();
  const seekRef = useSeekRef();
  const { clickCaptureProps, appRequiredDialogComponent } =
    useAppRequiredDialog();
  const canCommentInSpace = isAllowed(PERMISSION.TO_COMMENT_IN_SPACE, {
    space,
  });

  const isTyping = text.length > 0;

  const freezeTimestampWhileTyping = useMemo(() => {
    if (isTyping) return seekRef.current.getCurrentTime();

    return null;
  }, [isTyping, seekRef]);

  const onSave = async () => {
    if (!text || typeof freezeTimestampWhileTyping !== "number") return;

    setText("");

    const commentId = uuidv4();
    const commentData: Comment = {
      id: commentId,
      createdBy: getAuthId(),
      createdAt: Date.now(),
      spaceId,
      timestamp: freezeTimestampWhileTyping,
      trackId: track.id,
      trackVersionIds: currentTrackVersion?.id ? [currentTrackVersion.id] : [],
      blocks: [
        {
          type: COMMENT_BLOCK_TYPE.TEXT,
          text: text.trim(),
        },
      ],
    };

    await addSpaceComment({
      id: commentId,
      data: commentData,
    });

    joinEntity({
      entity: space,
      entityType: "Space",
      trigger: JOIN_ENTITY_TRIGGER.COMMENT,
    });
  };

  const CommentOptions = useMemo(() => {
    const commentBlocks = Object.values(COMMENT_BLOCKS).filter(
      (block) => block.inToolbar,
    );

    return (
      <div
        className="highnote-comment-input-buttons"
        data-is-disabled={!!focusedCardId}
        role="toolbar"
        aria-label="Comment input actions"
      >
        {commentBlocks.map((block) => (
          <CommentButton key={block.id} config={block} />
        ))}
      </div>
    );
  }, [focusedCardId]);

  const onClear = () => setText("");

  return (
    <>
      {appRequiredDialogComponent}
      <PermissionTooltip hasPermission={canCommentInSpace}>
        <div
          className="highnote-comment-input"
          data-is-typing={isTyping}
          // Below M, a tap opens the app-required dialog instead of composing on
          // the web (capture phase preempts inner controls). See useAppRequiredDialog.
          {...clickCaptureProps}
        >
          <TextInput
            value={text}
            isDisabled={!!focusedCardId}
            startIcon={
              <div className="highnote-comment-input-time">
                <CommentTimestamp
                  freezeTimestampWhileTyping={freezeTimestampWhileTyping}
                />
              </div>
            }
            onSubmit={onSave}
            placeholder="Add a comment"
            onChange={setText}
            endIcon={
              isTyping ? (
                <CommentInputActions
                  onClear={onClear}
                  onSave={onSave}
                  focusedCardId={focusedCardId}
                />
              ) : (
                CommentOptions
              )
            }
          />
        </div>
      </PermissionTooltip>
    </>
  );
};
